import { useEffect } from "react";
import { RadioButton } from "primereact/radiobutton";
import { useFormik } from "formik";
import * as yup from "yup";
import _ from "lodash";

import useCompanySetting from "../../../../hooks/company/useCompanySetting";

import { Input } from "../../../../components/Input/Input";
import { Toast } from "../../../../components/toast";

import { ContentRadio } from "./ContentRadio";
import SendButton from "./SendButton";

const NUMERIC_FIELDS = ["workCenterSize", "homeOfficeQuantity"];

const validationSchema = yup.object({
  restriction: yup.boolean().required("Este campo es obligatorio"),
  workCenterSize: yup.number().when("restriction", {
    is: false,
    then: (schema) =>
      schema
        .required("El valor de la geocerca es obligatorio")
        .min(0, "El valor mínimo de la geocerca es de 0 metros")
        .max(1000, "El valor máximo de la geocerca es de 1000 metros"),
    otherwise: (schema) => schema.notRequired(),
  }),
  homeOfficeQuantity: yup.number().when("restriction", {
    is: false,
    then: (schema) =>
      schema
        .required("La cantidad de lugares de trabajo es obligatoria")
        .min(0, "El valor mínimo de lugares de trabajo es 0")
        .max(3, "El valor máximo de lugares de trabajo es 3"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const WorkCenterRestriction = ({ ConfigChange, setConfigChange }) => {
  const { postCompanySettingAttendance, getCompanySettings, companySettings } =
    useCompanySetting();

  const initialValues = {
    verifyBiometricData: companySettings?.verifyBiometricData ?? false,
    toleranceBeforeEntry: companySettings?.toleranceBeforeEntry ?? 120,
    toleranceBeforeExit: companySettings?.toleranceBeforeExit ?? 60,
    toleranceAfterExit: companySettings?.toleranceAfterExit ?? 180,
    restriction: companySettings?.workCenterRestriction ?? true,
    workCenterSize: companySettings?.workCenterSize?.toString() ?? "500",
    homeOfficeQuantity: companySettings?.homeOfficeQuantity?.toString() ?? "3",
  };

  const { values, errors, handleChange, handleSubmit } = useFormik({
    initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const payload = {
        ...initialValues,
        workCenterRestriction: values.restriction,
        workCenterSize: values.restriction
          ? null
          : Number(values.workCenterSize),
        homeOfficeQuantity: values.restriction
          ? null
          : Number(values.homeOfficeQuantity),
      };

      postCompanySettingAttendance(payload).then((result) => {
        if (result && result.status === 200) {
          setConfigChange({
            ...ConfigChange,
            WorkCenterRestricitionChange: false,
          });
        }
      });
    },
  });

  const formHasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    getCompanySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formHasErrors) {
      if (errors.workCenterSize) {
        Toast("warning", errors.workCenterSize);
      }

      if (errors.homeOfficeQuantity) {
        Toast("warning", errors.homeOfficeQuantity);
      }
    }
  }, [formHasErrors, errors.homeOfficeQuantity, errors.workCenterSize]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;

    if (NUMERIC_FIELDS.includes(name)) {
      if (!/^(0|[1-9]\d*)?$/.test(value)) {
        return;
      }
    }

    const temporaryChanges = { ...values, [name]: value };
    const formValuesHasChanged = !_.isEqual(temporaryChanges, initialValues);

    handleChange(e);

    setConfigChange({
      ...ConfigChange,
      WorkCenterRestricitionChange: formValuesHasChanged,
    });
  };

  const renderRestrictionContent = () => {
    return (
      <div className="flex w-full flex-col h-44">
        <div className="flex h-full w-full flex-col">
          <p className="h-4">Tamaño de geocerca (metros)</p>
          <div className="flex flex-col pl-2 space-y-2">
            <div className="flex flex-row content-center items-center space-x-2">
              <Input
                value={values.workCenterSize}
                className="flex w-full h-4 !rounded-lg text-left"
                classNameContent="w-32 h-4 !rounded-lg"
                onChange={handleOnChange}
                error={!!errors.workCenterSize}
                name="workCenterSize"
              />
              <p>Metros</p>
            </div>
          </div>
        </div>
        <div className="flex h-full w-full flex-col">
          <p className="h-4">Cantidad de lugares de trabajo</p>
          <div className="flex flex-col pl-2 space-y-2">
            <div className="flex flex-row content-center items-center space-x-2">
              <Input
                value={values.homeOfficeQuantity}
                className="flex w-full h-4 !rounded-lg text-left"
                classNameContent="w-32 h-4 !rounded-lg"
                onChange={handleOnChange}
                error={!!errors.homeOfficeQuantity}
                name="homeOfficeQuantity"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex h-full w-full flex-col p-3 gap-2">
        <ContentRadio
          name="Permitir registro de asistencia en:"
          labelRadio1="Cualquier ubicación"
          labelRadio2="Dentro de sus centros y lugares de trabajo"
          id1="FreeZone"
          radio1={
            <RadioButton
              inputId="FreeZone"
              name="restriction"
              value={true}
              onChange={handleOnChange}
              checked={values.restriction === true}
            />
          }
          id2="OutZone"
          radio2={
            <RadioButton
              inputId="OutZone"
              name="restriction"
              value={false}
              onChange={handleOnChange}
              checked={values.restriction === false}
            />
          }
        />
        {values.restriction === false && renderRestrictionContent()}
      </div>
      <SendButton disabled={formHasErrors} onClick={handleSubmit} />
    </div>
  );
};

export default WorkCenterRestriction;
